import React from "react";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import useNormalizedURI from "../../hooks/useNormalizedURI";
import { removeTrailingSlash } from "../../utils/strings";
import { useLocation } from "react-router-dom";
import { GlobalStore } from "../../stores/global";
import URI from "urijs";

const getCanonicalUrl = uri => {
  const subdomain = uri.subdomain();
  return !subdomain || subdomain === "www" ? uri.hostname(uri.domain()).toString() : uri.toString();
};

const Meta = props => {
  const {
    passwordProtectedRoutes,
    defaultCountryName,
    currentCountry,
    currentCountryCode: countryCode
  } = GlobalStore.useState(c => c);

  const { pathname, state } = useLocation();
  const referrer = state?.referrer;
  const redirectUrl = state?.redirectUrl;

  const countryName = currentCountry?.name || defaultCountryName;
  const { keywords, index } = props;
  const four0Four = props.four0Four === true;
  const uri = useNormalizedURI();
  const title = props.title || process.env.REACT_APP_DESCRIPTION_SHORT;
  let description = props.description || process.env.REACT_APP_DESCRIPTION_MAIN;
  if (description.length > 160) description = description.substring(0, 157) + "...";
  const currentHost = uri.clone().segment([countryCode]);
  const canonical = four0Four ? null : `${removeTrailingSlash(props.canonical || getCanonicalUrl(uri))}`;
  const updated = props.updated ? dayjs(props.updated).unix() : undefined;
  const image = props.image || process.env.REACT_APP_LOGO_SVG;

  //get only English name of the country
  const siteName = props.siteName || `Speedoc ${countryName}`;
  const isProduction = process.env.REACT_APP_BUILD_ENV === "production";
  const slug = new URI(pathname).segment(0);
  const isPasswordProtected = passwordProtectedRoutes.includes(slug);
  let indexValue = isProduction && !isPasswordProtected ? "index" : "noindex";
  if (isProduction && index !== undefined) indexValue = index ? "index" : "noindex";
  let indexMetaSelector = document.querySelector('meta[name="robots"]');
  if (!indexMetaSelector) {
    indexMetaSelector = document.createElement("meta");
    indexMetaSelector.setAttribute("name", "robots");
    indexMetaSelector = document.getElementsByTagName("head")[0].appendChild(indexMetaSelector);
  }
  indexMetaSelector.setAttribute("content", indexValue);
  const dataJson = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${currentHost}/#organization`,
        name: siteName,
        url: `${currentHost}/`,
        sameAs: [],
        logo: {
          "@type": "ImageObject",
          "@id": `${currentHost}/#logo`,
          url: "https://public.speedoc.com/design/logo/logotype.svg",
          caption: siteName
        },
        image: { "@id": `${currentHost}/#logo` }
      },
      {
        "@type": "WebSite",
        "@id": `${currentHost}/#website`,
        url: `${currentHost}/`,
        name: siteName,
        publisher: { "@id": `${currentHost}/#organization` }
      },
      {
        "@type": "ImageObject",
        "@id": `${canonical || currentHost}/#primaryimage`,
        url: image
      },
      {
        "@type": "WebPage",
        "@id": `${canonical || currentHost}/#webpage`,
        url: canonical,
        name: title,
        isPartOf: { "@id": `${currentHost}/#website` },
        primaryImageOfPage: {
          "@id": `${canonical || currentHost}/#primaryimage`
        },
        dateModified: updated
      }
    ]
  });

  return (
    <Helmet>
      <title>{title}</title>
      {!four0Four ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:url" content={canonical || currentHost} />
      <meta property="fb:app_id" content={process.env.REACT_APP_FB_ID} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content={siteName} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}

      {referrer && redirectUrl ? <meta name="prerender-status-code" content="301" /> : null}
      {referrer && redirectUrl ? <meta name="prerender-header" content={`Location: ${redirectUrl}`} /> : null}

      {four0Four ? <meta name="prerender-status-code" content="404" /> : null}
      <meta property="og:title" content={title} />
      <meta property="og:type" content={props.type || "website"} />
      {updated ? <meta property="og:updated_time" content={updated} /> : null}
      {description ? <meta name="description" content={description} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:image" itemprop="image" content={image} />
      <meta property="og:image:secure_url" itemprop="image" content={image} />
      <meta property="og:image:alt" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: dataJson }} />
    </Helmet>
  );
};

export default ({ title, seo, type = "page", four0Four = false }) => (
  <Meta
    four0Four={four0Four}
    title={title}
    type={type}
    index={seo?.index}
    image={seo?.thumbnail?.formats?.medium?.url || seo?.thumbnail?.url}
    description={seo?.description}
  />
);
